/**
 * How many mirrors a vanity gets, where each one sits, and how wide it is.
 *
 * THE single decision point for vanity mirror COUNT and PLACEMENT. Two
 * pipelines build vanity accessories and they must not disagree:
 *
 *   - `optimize_vanity` (functions/.../tools/vanity.js) — the chat path
 *   - `bathroom-post-processing.js` — the SCAN path
 *
 * They did disagree. The scan path built exactly one mirror centred on the
 * cabinet, whatever the sink count, and skipped the width-matching rule
 * entirely for double sinks. Across dev that left 29 of 30 scan-built
 * double-sink vanities with a single 30" mirror over cabinets measuring
 * 32.6"–63.8", while 48 of 49 chat-built ones correctly had two. Both
 * callers now plan through here, so the divergence can't reopen.
 *
 * Pure — no object construction, no I/O. Callers turn a placement into
 * their own flavour of mirror object (procedural on the scan path,
 * catalog-or-procedural in optimize_vanity).
 */

const { INCHES_TO_METERS } = require('./constants');
const {
  calculateMatchedMirrorWidth,
  clampLateralWithinSpan,
} = require('./accessoryRules');
const { calculateDoubleVanityLayout } = require('../movement/resizeVanity');

/**
 * @param {Object}   options
 * @param {number}   options.cabinetWidth   - along-wall width, metres
 * @param {Object}   options.cabinetCenter  - { x, y, z }
 * @param {Array}    options.sinks          - the group's sink objects
 * @param {Function} options.projectOntoWall- (point) => along-wall scalar
 * @param {number}   [options.maxWallSpaceWidth] - upper bound from
 *   `calculateMirrorWallSpace`; omit/Infinity for "unbounded" (the scan
 *   path doesn't measure wall space).
 * @param {number}   [options.actualMirrorWidth] - the width the mirror will
 *   REALLY be, when the caller already knows it. `width` below is only a
 *   TARGET: since `e0564f0df` a catalog pick keeps its own product size and
 *   is merely queried BY the target, so a 70.9" target can resolve to a
 *   23.6" mirror. The lateral clamp must measure the real thing — clamping a
 *   23.6" mirror as though it were 70.9" wide says it cannot move at all.
 * @returns {Array<{suffix, sinkId, lateralCenter, width}>} one entry per
 *   mirror the vanity should have, ordered along the wall.
 *
 *   - `suffix`       — '' for the single mirror, '_0'/'_1'… for per-sink
 *                      ones, so callers mint stable, distinguishable ids.
 *   - `lateralCenter`— the point the mirror centres on along the wall.
 *   - `width`        — target along-wall width, metres.
 */
function planVanityMirrors({
  cabinetWidth,
  cabinetCenter,
  sinks,
  projectOntoWall,
  maxWallSpaceWidth,
  actualMirrorWidth,
}) {
  const basins = (sinks || []).filter((s) => s?.center);

  // 0 or 1 sink: one mirror, MATCHING the cabinet width in both directions
  // (user rule #10 — `calculateMatchedMirrorWidth` widens a narrow mirror as
  // well as narrowing a wide one), centred on the BASIN.
  //
  // Centring on the basin rather than the cabinet matters because a single
  // basin is NOT always centred in its cabinet: the layout pipeline slides
  // it along the wall so its standing clearance misses a toilet or a door
  // (`clearanceOffset`, `shared-lib/bathroom-analyzer/conversion.js`), and
  // `sinkLateralOffsets` carries that through to `assembleVanity`. This
  // branch used to hand back `cabinetCenter` unconditionally, which left the
  // mirror visibly off its own basin — a live 70.9" vanity carried a 23.6"
  // mirror 5.1" off the sink it belonged over.
  //
  // The clamp is what makes the rule degrade correctly rather than merely
  // bounding it: a mirror matched to the FULL cabinet width has nowhere to
  // go and stays exactly where it was, which is right — it already spans the
  // basin wherever the basin sits.
  if (basins.length < 2) {
    const width = calculateMatchedMirrorWidth(cabinetWidth, maxWallSpaceWidth);
    return [
      {
        suffix: '',
        sinkId: basins[0]?.id ?? null,
        lateralCenter: clampLateralWithinSpan({
          target: basins[0]?.center,
          spanCenter: cabinetCenter,
          spanWidth: cabinetWidth,
          itemWidth: actualMirrorWidth ?? width,
          projectOntoWall,
        }),
        width,
      },
    ];
  }

  // 2+ sinks: one mirror per sink, centred on its own basin. Width comes
  // from the established double-vanity case table (24"/27"/30" by cabinet
  // width) rather than a cabinet match — a double vanity's mirrors are
  // sized against the space each basin gets, not the whole run. Reusing
  // `calculateDoubleVanityLayout` keeps that table in one place.
  const layout = calculateDoubleVanityLayout({
    vanityWidth: cabinetWidth,
    vanityCenter: cabinetCenter,
    projectOntoWall,
  });
  const width = Math.min(
    layout.maxMirrorWidthInches * INCHES_TO_METERS,
    Number.isFinite(maxWallSpaceWidth) ? maxWallSpaceWidth : Infinity
  );

  return [...basins]
    .sort((a, b) => projectOntoWall(a.center) - projectOntoWall(b.center))
    .map((sink, i) => ({
      suffix: `_${i}`,
      sinkId: sink.id,
      lateralCenter: sink.center,
      width,
    }));
}

module.exports = { planVanityMirrors };
